import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { API_BASE as API, getToken } from '../config';
import { exportCvPdf, exportCvDocx, cvPreviewHtml, CvProfile, TailoredCv } from '../lib/cvExport';

interface TruthReport {
  score: number | null; // null = verification was unavailable when this CV was made
  counts: { verifiable: number; inferred: number; fabricated: number };
  claims: { claim: string; tier: string; rationale: string; source: string | null }[];
  note?: string;
}

interface CV {
  id: number;
  title: string;
  company: string | null;
  job_title: string | null;
  user_tags: string | null;
  latex_source: string | null;
  cover_letter: string | null;
  truth_report: TruthReport | null;
  tailored_json: TailoredCv | null;
  content_preview: string | null;
  created_at: string;
}

const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const MyCVsPage: React.FC = () => {
  const [cvs, setCvs] = useState<CV[]>([]);
  const [profile, setProfile] = useState<CvProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<CV | null>(null);
  const [tab, setTab] = useState<'cv' | 'cover' | 'truth'>('cv');
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [newTitle, setNewTitle] = useState('');

  // The saved profile provides the export header (name, contact, education).
  useEffect(() => {
    fetch(`${API}/api/cv-profile/`, { headers: authHeaders() })
      .then(r => r.json())
      .then(p => setProfile(p || null))
      .catch(() => {});
  }, []);

  const downloadPdf = (cv: CV) => {
    if (!cv.tailored_json) return;
    try {
      exportCvPdf(profile ?? {}, cv.tailored_json, cv.company || undefined);
    } catch (e: any) {
      toast.error(e.message ?? 'PDF export failed.');
    }
  };

  const downloadWord = async (cv: CV) => {
    if (!cv.tailored_json) return;
    try {
      await exportCvDocx(profile ?? {}, cv.tailored_json, cv.company || undefined);
      toast.success('Word document downloaded.');
    } catch (e: any) {
      toast.error(e.message ?? 'Word export failed.');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/cvs/`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load CVs');
      setCvs(data);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rename = async (id: number) => {
    if (!newTitle.trim()) return setRenamingId(null);
    try {
      const res = await fetch(`${API}/api/cvs/${id}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle.trim() }),
      });
      if (!res.ok) throw new Error('Rename failed');
      toast.success('Renamed');
      setRenamingId(null);
      load();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this CV? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API}/api/cvs/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Deleted');
      if (open?.id === id) setOpen(null);
      load();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const copy = (text: string, what: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  };

  const scoreColor = (s: number | null) =>
    s == null ? 'text-gray-500' : s >= 75 ? 'text-green-600' : s >= 50 ? 'text-yellow-600' : 'text-red-600';

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">My CVs</h1>
      <p className="text-gray-600 mb-6">Every CV you generate is saved here. Open, rename, copy or delete.</p>

      {loading && <p className="text-gray-500 py-8 text-center">Loading…</p>}

      {!loading && cvs.length === 0 && (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-xl">
          <div className="text-4xl mb-3">📂</div>
          <p className="text-gray-600">No CVs yet. Generate one from <b>Create CV</b> or <b>Browse Jobs</b> — it'll appear here.</p>
        </div>
      )}

      <div className="space-y-3">
        {cvs.map((cv) => (
          <div key={cv.id} className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                {renamingId === cv.id ? (
                  <div className="flex gap-2">
                    <input
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && rename(cv.id)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                      autoFocus
                    />
                    <button onClick={() => rename(cv.id)} className="text-sm text-blue-600 font-medium">Save</button>
                    <button onClick={() => setRenamingId(null)} className="text-sm text-gray-500">Cancel</button>
                  </div>
                ) : (
                  <h3 className="font-semibold text-gray-900 truncate">{cv.title}</h3>
                )}
                <p className="text-xs text-gray-500 mt-0.5">
                  {new Date(cv.created_at).toLocaleDateString()}
                  {cv.truth_report && (
                    cv.truth_report.score == null
                      ? <> · <span className="text-gray-400">not verified</span></>
                      : <> · Truth score: <b className={scoreColor(cv.truth_report.score)}>{cv.truth_report.score}/100</b></>
                  )}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => { setOpen(open?.id === cv.id ? null : cv); setTab('cv'); }}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                  {open?.id === cv.id ? 'Close' : 'Open'}
                </button>
                <button onClick={() => { setRenamingId(cv.id); setNewTitle(cv.title); }}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                  Rename
                </button>
                <button onClick={() => remove(cv.id)}
                  className="px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50">
                  Delete
                </button>
              </div>
            </div>

            {open?.id === cv.id && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <div className="flex gap-2 mb-3">
                  {(['cv', 'cover', 'truth'] as const).map((t) => (
                    <button key={t} onClick={() => setTab(t)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${tab === t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                      {t === 'cv' ? 'CV' : t === 'cover' ? 'Cover letter' : 'Truth check'}
                    </button>
                  ))}
                </div>

                {tab === 'cv' && (
                  <div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {cv.tailored_json && (
                        <>
                          <button onClick={() => downloadPdf(cv)}
                            className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700">
                            Save as PDF
                          </button>
                          <button onClick={() => downloadWord(cv)}
                            className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
                            Download Word (.docx)
                          </button>
                        </>
                      )}
                      <button onClick={() => copy(cv.latex_source || '', 'CV LaTeX')}
                        className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
                        Copy LaTeX
                      </button>
                    </div>

                    {cv.tailored_json ? (
                      <iframe
                        title={`CV preview — ${cv.title}`}
                        srcDoc={cvPreviewHtml(profile ?? {}, cv.tailored_json, cv.company || undefined)}
                        className="w-full h-[520px] bg-white border border-gray-200 rounded-lg"
                        sandbox=""
                      />
                    ) : (
                      <div>
                        <p className="text-xs text-amber-600 mb-2">
                          This CV was saved before visual previews existed — only its LaTeX source is stored.
                          Regenerate it to get the document view and PDF/Word export.
                        </p>
                        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs overflow-auto max-h-80 whitespace-pre-wrap">{cv.latex_source || '(empty)'}</pre>
                      </div>
                    )}
                  </div>
                )}
                {tab === 'cover' && (
                  <div>
                    <button onClick={() => copy(cv.cover_letter || '', 'Cover letter')}
                      className="mb-2 text-xs text-blue-600 hover:underline">Copy to clipboard</button>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm whitespace-pre-wrap max-h-80 overflow-auto">{cv.cover_letter || '(empty)'}</div>
                  </div>
                )}
                {tab === 'truth' && (
                  <div className="max-h-80 overflow-auto">
                    {cv.truth_report && cv.truth_report.score == null ? (
                      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                        ⚠️ This CV wasn't fact-checked (the verifier was unavailable when it was generated).
                      </p>
                    ) : cv.truth_report ? (
                      <>
                        <p className="text-sm mb-2">
                          Score: <b className={scoreColor(cv.truth_report.score)}>{cv.truth_report.score}/100</b>
                          {' '}· ✅ {cv.truth_report.counts.verifiable} verifiable
                          {' '}· 🟡 {cv.truth_report.counts.inferred} inferred
                          {' '}· 🔴 {cv.truth_report.counts.fabricated} fabricated
                        </p>
                        <div className="space-y-2">
                          {cv.truth_report.claims.map((cl, i) => (
                            <div key={i} className={`text-xs p-2 rounded border ${cl.tier === 'fabricated' ? 'border-red-200 bg-red-50' : cl.tier === 'inferred' ? 'border-yellow-200 bg-yellow-50' : 'border-green-200 bg-green-50'}`}>
                              <b>{cl.tier}</b>: {cl.claim}
                              {cl.rationale && <div className="text-gray-500 mt-0.5">{cl.rationale}</div>}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-gray-500">No truth report for this CV.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyCVsPage;
